import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getStripe } from "~/lib/stripe";
import { z } from "zod";
import { upsertContact } from "~/lib/ghl/upsert-contact";
import { getLocationAccessToken } from "~/lib/ghl/get-location-access-token";
import { resources } from "~/../drizzle/schema";
import { count, eq } from "drizzle-orm";
import { createServerFn } from "@tanstack/react-start";
import { CoachProfileLayout } from "~/components/layouts/coach-profile-layout";

// Server function to create checkout session
export const createCheckout = createServerFn({ method: "POST" })
  .validator(z.object({ offerId: z.string(), coachId: z.string() }))
  .handler(async ({ data }) => {
    const { offerId, coachId } = data;

    const { getDBWithInit } = await import("~/lib/db");
    const coach = await (
      await getDBWithInit()
    ).query.coaches.findFirst({
      where: (coaches, { eq }) => eq(coaches.id, coachId),
    });

    if (!coach) {
      throw new Error("Coach not found");
    }

    const offer = await (
      await getDBWithInit()
    ).query.offers.findFirst({
      where: (offers, { eq }) => eq(offers.id, offerId),
    });

    if (!offer || !offer.directSaleEnabled) {
      throw new Error("Offer not found or not available for direct purchase");
    }

    if (!coach.stripeAccountId || !coach.stripeChargesEnabled) {
      throw new Error("Coach is not set up for payments");
    }

    const stripe = await getStripe();
    const baseUrl = process.env.VITE_APP_URL;
    if (!baseUrl) {
      throw new Error("VITE_APP_URL is not set");
    }

    // Create a Stripe Checkout Session
    const session = await stripe.checkout.sessions.create(
      {
        mode: "payment",
        line_items: [
          {
            price_data: {
              currency: offer.currency!.toLowerCase(),
              product_data: {
                name: offer.headline || "Coaching Offer",
                description: offer.description || undefined,
              },
              unit_amount: offer.price!,
            },
            quantity: 1,
          },
        ],
        payment_intent_data: {
          application_fee_amount: Math.round(
            offer.price! *
              parseFloat(process.env.VITE_APPLICATION_FEE_RATIO || "0.1"),
          ),
          metadata: {
            type: "direct_offer_purchase",
            coachId: coach.id,
            offerId: offer.id,
          },
        },
        success_url: `${baseUrl}/${coach.username}/offer?success=true`,
        cancel_url: `${baseUrl}/${coach.username}/offer`,
        customer_creation: "always",
        invoice_creation: {
          enabled: true,
        },
        metadata: {
          type: "direct_offer_purchase",
          coachId: coach.id,
          offerId: offer.id,
        },
      },
      { stripeAccount: coach.stripeAccountId },
    );

    return {
      success: true,
      checkoutUrl: session.url!,
    };
  });

export const LeadMagnetFormSchema = z.object({
  firstName: z.string().min(1),
  email: z.string().email(),
  coachId: z.string().min(1),
});

export type LeadMagnetForm = z.infer<typeof LeadMagnetFormSchema>;

export const submitLeadAction = createServerFn({ method: "POST" })
  .validator(LeadMagnetFormSchema)
  .handler(async ({ data }) => {
    const { firstName, email, coachId } = data;
    const { getCoachByUsername, getCoachByLocationId } = await import(
      "~/lib/server/coach"
    );

    const coach =
      (await getCoachByLocationId(coachId)) ||
      (await getCoachByUsername(coachId));
    if (!coach) {
      throw new Error("Coach not found");
    }

    // Ensure we have a coach with ghlLocationId (from getCoachByLocationId)
    if (!("ghlLocationId" in coach) || !coach.ghlLocationId) {
      throw new Error("Coach does not have location id set");
    }

    const accessToken = await getLocationAccessToken(coach.ghlLocationId);

    await upsertContact(
      {
        firstName,
        email,
        locationId: coach.ghlLocationId,
        tags: ["source/dcm-lead"],
      },
      accessToken,
    );

    const { getDBWithInit } = await import("~/lib/db");
    const profile = await (
      await getDBWithInit()
    ).query.profiles.findFirst({
      where: (profile, { eq }) => eq(profile.coachId, coach.id),
      columns: {
        leadMagnetUrl: true,
      },
    });

    if (!profile?.leadMagnetUrl) {
      throw new Error("Lead magnet URL not found");
    }

    return {
      url: profile.leadMagnetUrl,
    };
  });

export const getResourceCount = createServerFn({ method: "GET" })
  .validator(z.object({ username: z.string() }))
  .handler(async ({ data }) => {
    const { username } = data;
    const { getCoachByUsername } = await import("~/lib/server/coach");
    const coach = await getCoachByUsername(username);

    if (!coach) {
      return 0;
    }

    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const result = await db
      .select({ value: count() })
      .from(resources)
      .where(eq(resources.coachId, coach.id));

    return result[0]?.value || 0;
  });

export const getCoachProfile = createServerFn({ method: "GET" })
  .validator(z.object({ username: z.string() }))
  .handler(async ({ data }) => {
    const { username } = data;
    const { getCoachByUsername } = await import("~/lib/server/coach");
    const coach = await getCoachByUsername(username);

    if (!coach) {
      throw new Error("Coach not found");
    }

    return coach;
  });

export const Route = createFileRoute("/$username/_profile")({
  loader: async ({ context, params }) => {
    // Pre-load coach profile and resource count
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["coach-profile", params.username],
        queryFn: () => getCoachProfile({ data: { username: params.username } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["resource-count", params.username],
        queryFn: () =>
          getResourceCount({ data: { username: params.username } }),
      }),
    ]);
    return {};
  },
  component: ProfileLayout,
});

interface ProfileLayoutProps {
  routePath?: string;
}

// Shared layout component that can be used in both routes
function ProfileLayout({ routePath = "" }: ProfileLayoutProps) {
  const { username } = Route.useParams();

  const { data: coach } = useQuery({
    queryKey: ["coach-profile", username],
    queryFn: () => getCoachProfile({ data: { username } }),
  });

  const { data: resourceCount = 0 } = useQuery({
    queryKey: ["resource-count", username],
    queryFn: () => getResourceCount({ data: { username } }),
  });

  if (!coach?.profile || !coach?.details) {
    return <div>Loading...</div>;
  }

  // For some reason the type inference is not working here but this works
  const coachWithRequiredData = {
    ...coach,
    profile: coach.profile,
    details: coach.details,
  };

  return (
    <CoachProfileLayout
      coach={coachWithRequiredData}
      resourceCount={resourceCount}
      routePath={routePath}
    >
      <Outlet />
    </CoachProfileLayout>
  );
}
